/**
 * Centralized WhatsApp deep-link helpers + message templates.
 *
 * Add new templates by extending WHATSAPP_TEMPLATES below. Use the
 * <WhatsAppButton /> component from components/ui/whatsapp-button.jsx
 * to render — don't construct wa.me URLs by hand in components.
 *
 * The wa.me protocol opens the WhatsApp app (mobile) or web (desktop)
 * with a chat pre-filled. If `phone` is omitted, WhatsApp shows a
 * contact picker.
 */

/**
 * Normalize a phone number for wa.me:
 *  - Strip every non-digit char
 *  - Prefix country code (default: India 91) if it looks like a
 *    plain 10-digit number
 *  - If it already starts with a country code, leave alone
 */
export function normalizeWhatsAppPhone(phone, defaultCountryCode = '91') {
  if (!phone) return '';
  const digits = String(phone).replace(/\D/g, '');
  if (!digits) return '';
  // India: 10 digits = local; prefix country code
  if (digits.length === 10) return `${defaultCountryCode}${digits}`;
  return digits;
}

/**
 * Build a wa.me share URL. Pass empty/omitted `phone` to let WhatsApp
 * show its contact picker (user picks the recipient).
 */
export function whatsappUrl({ phone, message }) {
  const number = normalizeWhatsAppPhone(phone);
  const text = encodeURIComponent(message ?? '');
  return number
    ? `https://wa.me/${number}?text=${text}`
    : `https://wa.me/?text=${text}`;
}

/**
 * All user-visible WhatsApp message templates.
 *
 * Each template is a function — pass it the variables it needs, get
 * back the final string. Keeps copy in one place so non-developers
 * can edit wording easily.
 */
export const WHATSAPP_TEMPLATES = {
  /**
   * Admin → Service Provider: "Submit a quote for this customer's request."
   * Used in AdminServiceRequests.jsx provider rows.
   */
  providerQuoteRequest({ providerName, customerName, category, problemTitle, url }) {
    return [
      `Hi${providerName ? ` ${providerName}` : ''},`,
      ``,
      `*CHIV Properties* has a new *${category}* service request for you:`,
      ``,
      `📌 *${problemTitle}*`,
      `👤 Customer: ${customerName}`,
      ``,
      `Please review the details and submit your quote here:`,
      url,
      ``,
      `Reply to this chat if you have any questions.`,
      `— CHIV Properties & Works Ltd.`,
    ].join('\n');
  },

  /**
   * Customer → Self (or family member): "Save this tracking link."
   * Used on ServiceRequest.jsx success page.
   */
  customerTrackRequest({ customerName, problemTitle, url }) {
    return [
      `Hi${customerName ? ` ${customerName}` : ''},`,
      ``,
      `Your service request has been submitted with *CHIV Properties*:`,
      ``,
      `📌 ${problemTitle}`,
      ``,
      `Track your request and view quotes here:`,
      url,
      ``,
      `Save this link — you'll see provider quotes as they come in.`,
      `— CHIV Properties & Works Ltd.`,
    ].join('\n');
  },

  // Future templates go below. Examples we may add later:
  //
  // customerContactProvider({ providerName, providerPhone, problemTitle, url }) { ... }
  // propertyInquiry({ propertyName, propertyLocation }) { ... }
  // generalBusinessChat() { ... }
};
